import { useState } from 'react';
import { Loader2, Wand2, Sparkles, Youtube, Check, RefreshCw, ImagePlus, Clapperboard, UploadCloud, ImageOff } from 'lucide-react';
import { toast } from 'sonner';

const IMAGE_STYLES = [
  { key: 'photoreal_static', labelFr: '📷 Photoréaliste — statique', labelEn: '📷 Photoreal — static' },
  { key: 'photoreal_dynamic', labelFr: '🎬 Photoréaliste — pose dynamique', labelEn: '🎬 Photoreal — dynamic pose' },
  { key: 'schematic', labelFr: '🧍 Schématique — début/fin du mouvement', labelEn: '🧍 Schematic — start/end of movement' },
  { key: 'muscle_storyboard', labelFr: '🔥 Storyboard musculaire — 3 poses (muscles en rouge)', labelEn: '🔥 Muscle storyboard — 3 poses (muscles in red)' },
];

const ACCEPTED_UPLOAD_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Lit un fichier image local (validé JPG/PNG/WebP, 5 Mo max) et renvoie { base64, contentType,
 * previewUrl } — utilisé pour l'upload "telle quelle" ET la référence IA. */
const readUploadFile = (file) => new Promise((resolve, reject) => {
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) { reject("Format non supporté (JPG, PNG ou WebP uniquement)."); return; }
  if (file.size > MAX_UPLOAD_BYTES) { reject('Image trop volumineuse (5 Mo maximum).'); return; }
  const reader = new FileReader();
  reader.onload = () => resolve({ base64: reader.result.split(',')[1], contentType: file.type, previewUrl: reader.result });
  reader.onerror = () => reject('Lecture du fichier échouée.');
  reader.readAsDataURL(file);
});

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const extractErrorDetail = async (res) => {
  try { const data = await res.json(); return data?.detail; } catch { return null; }
};

// Outils admin d'un exercice (image, texte d'exécution, vidéo YouTube) — utilisés à la fois
// dans le détail d'un exercice existant ET juste après la création d'un nouvel exercice.
export const ExerciseAdminTools = ({ exercise, en, authFetch, onLocalUpdate }) => {
  const L = (fr, eng) => (en ? eng : fr);

  // --- Relecture admin (purement informatif, cf. ReviewStatusBadge) ---
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const markReviewed = async () => {
    setMarkingReviewed(true);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}/mark-reviewed`, { method: 'POST' });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      onLocalUpdate({ admin_reviewed_at: new Date().toISOString() });
      toast.success(L('Marqué comme relu', 'Marked as reviewed'));
    } catch (e) { toast.error(e.message || L('Échec', 'Failed')); }
    finally { setMarkingReviewed(false); }
  };

  // --- Image (galerie de candidats à choix multiples) ---
  const [imgStyle, setImgStyle] = useState('photoreal_static');
  const [imgFeedback, setImgFeedback] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [generatingImg, setGeneratingImg] = useState(false);
  const [confirmingImg, setConfirmingImg] = useState(false);
  const [refImage, setRefImage] = useState(null); // { base64, contentType, previewUrl }
  const [refImageErr, setRefImageErr] = useState('');
  const [uploadingAsIs, setUploadingAsIs] = useState(false);

  const uploadAsIs = async () => {
    if (!refImage) return;
    setUploadingAsIs(true);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}/upload-image`, {
        method: 'POST', body: JSON.stringify({ image_base64: refImage.base64, content_type: refImage.contentType }),
      });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      const data = await res.json();
      onLocalUpdate({
        image_url: data.image_url, image_path: data.image_path, image_mode: data.image_mode,
        image_status: data.image_status, image_history: data.image_history,
      });
      setRefImage(null);
      toast.success(L('Image importée (upload direct)', 'Image imported (direct upload)'));
    } catch (e) { toast.error(e.message || L("Échec de l'upload", 'Upload failed')); }
    finally { setUploadingAsIs(false); }
  };

  const generateCandidate = async () => {
    setGeneratingImg(true);
    try {
      const endpoint = refImage ? `/api/exercises/${exercise.id}/generate-image-candidate-from-upload` : `/api/exercises/${exercise.id}/generate-image-candidate`;
      const body = refImage
        ? { style: imgStyle, feedback: imgFeedback.trim(), image_base64: refImage.base64, content_type: refImage.contentType }
        : { style: imgStyle, feedback: imgFeedback.trim() };
      const res = await authFetch(endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      const data = await res.json();
      setCandidates((prev) => [...prev, data]);
      setSelectedCandidate(data);
    } catch (e) { toast.error(e.message || L('Échec de la génération de l\'image', 'Image generation failed')); }
    finally { setGeneratingImg(false); }
  };

  const confirmCandidate = async () => {
    if (!selectedCandidate) return;
    setConfirmingImg(true);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}/confirm-image`, {
        method: 'POST', body: JSON.stringify(selectedCandidate),
      });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      const refreshed = await authFetch(`/api/exercises/${exercise.id}`).then((r) => r.json()).catch(() => null);
      onLocalUpdate({
        image_url: selectedCandidate.image_url, image_path: selectedCandidate.image_path,
        image_mode: selectedCandidate.style, image_status: 'ready',
        image_history: refreshed?.image_history || exercise.image_history,
      });
      setCandidates([]);
      setSelectedCandidate(null);
      setImgFeedback('');
      toast.success(L('Nouvelle image enregistrée', 'New image saved'));
    } catch (e) { toast.error(e.message || L('Échec de l\'enregistrement de l\'image', 'Failed to save image')); }
    finally { setConfirmingImg(false); }
  };

  // --- Historique des images (3 dernières versions confirmées, restaurables) ---
  const [revertingIdx, setRevertingIdx] = useState(null);
  const [pendingRevertIdx, setPendingRevertIdx] = useState(null);
  const revertImage = async (idx) => {
    setRevertingIdx(idx);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}/revert-image`, {
        method: 'POST', body: JSON.stringify({ history_index: idx }),
      });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      const data = await res.json();
      onLocalUpdate({
        image_url: data.image_url, image_path: data.image_path, image_mode: data.image_mode,
        image_status: data.image_status, image_history: data.image_history,
      });
      toast.success(L('Image restaurée', 'Image restored'));
    } catch (e) { toast.error(e.message || L('Échec de la restauration', 'Failed to restore')); }
    finally { setRevertingIdx(null); }
  };

  // --- Texte d'exécution (IA, éditable avant enregistrement) ---
  const [generatingText, setGeneratingText] = useState(false);
  const [draftText, setDraftText] = useState(null); // { execution, tips: [] }
  const [savingText, setSavingText] = useState(false);

  const generateText = async () => {
    setGeneratingText(true);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}/generate-execution-text`, { method: 'POST' });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      const data = await res.json();
      setDraftText({ execution: data.execution || '', tips: data.tips && data.tips.length ? data.tips : [''] });
    } catch (e) { toast.error(e.message || L('Échec de la génération du texte', 'Text generation failed')); }
    finally { setGeneratingText(false); }
  };

  const saveText = async () => {
    if (!draftText) return;
    setSavingText(true);
    try {
      const tips = draftText.tips.map((t) => t.trim()).filter(Boolean);
      const res = await authFetch(`/api/exercises/${exercise.id}`, {
        method: 'PATCH', body: JSON.stringify({ execution: draftText.execution.trim(), tips }),
      });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      onLocalUpdate({ execution: draftText.execution.trim(), tips });
      setDraftText(null);
      toast.success(L('Texte enregistré', 'Text saved'));
    } catch (e) { toast.error(e.message || L('Échec de l\'enregistrement du texte', 'Failed to save text')); }
    finally { setSavingText(false); }
  };

  // --- Vidéo YouTube (recherche + choix manuel) ---
  const [searchingYt, setSearchingYt] = useState(false);
  const [ytCandidates, setYtCandidates] = useState(null);

  const searchYoutube = async () => {
    setSearchingYt(true);
    setYtCandidates(null);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}/youtube-candidates`);
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      const data = await res.json();
      setYtCandidates(data.results || []);
    } catch (e) { toast.error(e.message || L('Recherche YouTube échouée', 'YouTube search failed')); }
    finally { setSearchingYt(false); }
  };

  const attachYoutube = async (url) => {
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}`, {
        method: 'PATCH', body: JSON.stringify({ youtube_url: url }),
      });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      onLocalUpdate({ youtube_url: url });
      setYtCandidates(null);
      toast.success(L('Vidéo YouTube attachée', 'YouTube video attached'));
    } catch (e) { toast.error(e.message || L('Échec de l\'attachement', 'Failed to attach')); }
  };

  // --- Visibilité vidéo pour ce client précis (dérogation au switch global) ---
  const currentVisibility = exercise.video_disabled ? 'hidden' : exercise.video_enabled_override ? 'forced' : 'default';
  const [savingVisibility, setSavingVisibility] = useState(false);
  const saveVideoVisibility = async (value) => {
    const patch = value === 'hidden' ? { video_disabled: true, video_enabled_override: false }
      : value === 'forced' ? { video_disabled: false, video_enabled_override: true }
      : { video_disabled: false, video_enabled_override: false };
    setSavingVisibility(true);
    try {
      const res = await authFetch(`/api/exercises/${exercise.id}`, { method: 'PATCH', body: JSON.stringify(patch) });
      if (!res.ok) throw new Error(await extractErrorDetail(res));
      onLocalUpdate(patch);
      toast.success(L('Visibilité mise à jour', 'Visibility updated'));
    } catch (e) { toast.error(e.message || L('Échec de la mise à jour', 'Failed to update')); }
    finally { setSavingVisibility(false); }
  };

  return (
    <div className="space-y-4" data-testid="exercise-admin-tools">
      <button onClick={markReviewed} disabled={markingReviewed}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-sky-300 bg-sky-500/10 border border-sky-500/30 hover:bg-sky-500/20 disabled:opacity-50"
        data-testid="mark-reviewed-btn">
        {markingReviewed ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        {L('Marquer comme relu', 'Mark as reviewed')}
      </button>

      <div className="space-y-1.5" data-testid="video-visibility-override">
        <p className="text-xs font-bold text-zinc-300 uppercase flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5" /> {L('Visibilité vidéo (client)', 'Video visibility (client)')}</p>
        <p className="text-[11px] text-zinc-500">{L('Par défaut, suit le switch global. Vous pouvez forcer un état pour cet exercice précis.', 'Follows the global switch by default. You can force a state for this specific exercise.')}</p>
        <div className="flex gap-1.5">
          {[
            { v: 'default', l: L('Réglage global', 'Global setting') },
            { v: 'forced', l: L('Toujours afficher', 'Always show') },
            { v: 'hidden', l: L('Toujours masquer', 'Always hide') },
          ].map((opt) => (
            <button key={opt.v} onClick={() => saveVideoVisibility(opt.v)} disabled={savingVisibility}
              className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold border transition-colors disabled:opacity-50 ${currentVisibility === opt.v ? 'bg-fuchsia-600/20 border-fuchsia-500/50 text-fuchsia-200' : 'border-white/10 text-zinc-400 hover:border-fuchsia-500/30'}`}
              data-testid={`video-visibility-${opt.v}-btn`}>
              {opt.l}
            </button>
          ))}
        </div>
      </div>

      {/* Texte d'exécution — TOUJOURS généré en premier (ancre le prompt image ET vidéo) */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-zinc-300 uppercase flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {L("1. Texte d'exécution", '1. Execution text')}</p>
        <button onClick={generateText} disabled={generatingText}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-zinc-800 border border-white/10 hover:border-primary/40 disabled:opacity-50"
          data-testid="generate-text-btn">
          {generatingText ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {L('Générer un texte (IA)', 'Generate text (AI)')}
        </button>
        {draftText && (
          <div className="space-y-2 bg-zinc-800/50 border border-white/10 rounded-lg p-3" data-testid="text-draft-editor">
            <textarea value={draftText.execution}
              onChange={(e) => setDraftText((d) => ({ ...d, execution: e.target.value }))}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2 text-sm text-white min-h-[80px]"
              data-testid="text-draft-execution" />
            {draftText.tips.map((t, i) => (
              <input key={i} value={t}
                onChange={(e) => setDraftText((d) => ({ ...d, tips: d.tips.map((x, j) => j === i ? e.target.value : x) }))}
                className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2 text-xs text-white"
                placeholder={L(`Conseil ${i + 1}`, `Tip ${i + 1}`)}
                data-testid={`text-draft-tip-${i}`} />
            ))}
            <div className="flex items-center gap-2">
              <button onClick={saveText} disabled={savingText}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 disabled:opacity-50"
                data-testid="save-text-btn">
                {savingText ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {L('Enregistrer', 'Save')}
              </button>
              <button onClick={() => setDraftText(null)} className="text-xs text-zinc-400 hover:text-white" data-testid="cancel-text-btn">
                {L('Annuler', 'Cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Image — nécessite le texte d'exécution (chaînage séquentiel Texte -> Image -> Vidéo) */}
      <div className="pt-3 border-t border-white/5 space-y-2">
        <p className="text-xs font-bold text-zinc-300 uppercase flex items-center gap-1.5"><ImagePlus className="w-3.5 h-3.5" /> {L('2. Image', '2. Image')}</p>

        <div data-testid="image-upload-section">
          {!refImage ? (
            <label className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-white/15 rounded-lg py-4 cursor-pointer hover:border-primary/40 transition-colors" data-testid="image-upload-dropzone">
              <UploadCloud className="w-5 h-5 text-zinc-500" />
              <span className="text-[11px] text-zinc-500 text-center px-3">{L('Uploader une photo (JPG/PNG/WebP, 5 Mo max) — telle quelle ou comme référence IA', 'Upload a photo (JPG/PNG/WebP, 5 MB max) — as-is or as an AI reference')}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; setRefImageErr(''); readUploadFile(f).then(setRefImage).catch(setRefImageErr); }}
                data-testid="image-upload-input" />
            </label>
          ) : (
            <div className="flex items-center gap-3 bg-zinc-800/60 border border-white/10 rounded-lg p-2.5" data-testid="image-upload-preview">
              <img src={refImage.previewUrl} alt="référence" className="w-14 h-14 rounded-lg object-cover border border-white/10" />
              <p className="flex-1 text-[11px] text-zinc-300">{L('Image chargée — utilisez les boutons ci-dessous.', 'Image loaded — use the buttons below.')}</p>
              <button onClick={() => setRefImage(null)} className="text-zinc-500 hover:text-white shrink-0" data-testid="image-upload-remove-btn">
                <ImageOff className="w-4 h-4" />
              </button>
            </div>
          )}
          {refImageErr && <p className="text-[11px] text-red-400 mt-1" data-testid="image-upload-error">{refImageErr}</p>}
          {refImage && (
            <button onClick={uploadAsIs} disabled={uploadingAsIs}
              className="w-full mt-2 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
              data-testid="image-upload-as-is-btn">
              {uploadingAsIs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {L('Garder cette image telle quelle (0 crédit)', 'Keep this image as-is (0 credit)')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select value={imgStyle} onChange={(e) => setImgStyle(e.target.value)}
            disabled={!exercise.execution}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50"
            data-testid="image-style-select">
            {IMAGE_STYLES.map((s) => <option key={s.key} value={s.key}>{en ? s.labelEn : s.labelFr}</option>)}
          </select>
          <button onClick={generateCandidate} disabled={generatingImg || !exercise.execution}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50"
            data-testid="generate-image-candidate-btn">
            {generatingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {refImage ? L('Générer avec IA (réf. image)', 'Generate with AI (image ref)') : L('Générer un candidat', 'Generate a candidate')}
          </button>
        </div>
        <textarea value={imgFeedback} onChange={(e) => setImgFeedback(e.target.value)}
          disabled={!exercise.execution}
          placeholder={L(
            "Demande spéciale ou correction à prendre en compte (optionnel) — ex: \"vue plus de face pour bien voir le ballon entre les 2 genoux\"",
            'Special request or correction to take into account (optional) — e.g. "more front-facing view to clearly show the ball between both knees"'
          )}
          className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2 text-xs text-white placeholder:text-zinc-500 disabled:opacity-50"
          rows={2}
          data-testid="image-feedback-textarea" />
        {!exercise.execution && (
          <p className="text-[11px] text-amber-400 italic" data-testid="image-blocked-hint">
            {L("Générez d'abord le texte d'exécution ci-dessus — il est utilisé pour ancrer la posture de l'image.",
               'Generate the execution text above first — it is used to anchor the pose in the image.')}
          </p>
        )}
        {candidates.length > 0 && (
          <>
            <div className="flex items-center gap-2 flex-wrap" data-testid="image-candidates-strip">
              {candidates.map((c, i) => (
                <button key={i} onClick={() => setSelectedCandidate(c)}
                  className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 ${selectedCandidate === c ? 'border-primary' : 'border-white/10'}`}
                  data-testid={`image-candidate-${i}`}>
                  <img src={`${backendUrl}${c.image_url}`} alt="" className="w-full h-full object-cover" />
                  {selectedCandidate === c && <span className="absolute top-0.5 right-0.5 bg-primary rounded-full p-0.5"><Check className="w-2.5 h-2.5 text-white" /></span>}
                </button>
              ))}
            </div>
            {selectedCandidate && exercise.image_url && (
              <div className="grid grid-cols-2 gap-2" data-testid="image-before-after-comparator">
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase text-center">{L('Avant (actuelle)', 'Before (current)')}</p>
                  <img src={`${backendUrl}${exercise.image_url}`} alt="avant" className="w-full aspect-square object-contain rounded-lg bg-zinc-900 border border-white/10" data-testid="comparator-before-image" />
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-primary uppercase text-center">{L('Après (candidat)', 'After (candidate)')}</p>
                  <img src={`${backendUrl}${selectedCandidate.image_url}`} alt="après" className="w-full aspect-square object-contain rounded-lg bg-zinc-900 border border-primary/40" data-testid="comparator-after-image" />
                </div>
              </div>
            )}
            <button onClick={confirmCandidate} disabled={!selectedCandidate || confirmingImg}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 disabled:opacity-50"
              data-testid="confirm-image-btn">
              {confirmingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {L('Utiliser cette image', 'Use this image')}
            </button>
          </>
        )}
        <p className="text-[11px] text-zinc-500 italic">{L(
          "Générez autant de candidats que vous voulez (styles différents possibles), puis choisissez celui à enregistrer.",
          'Generate as many candidates as you like (different styles possible), then pick the one to save.'
        )}</p>

        {exercise.image_history && exercise.image_history.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-white/5" data-testid="image-history-section">
            <p className="text-[11px] font-bold text-zinc-500 uppercase">{L('Versions précédentes', 'Previous versions')}</p>
            <div className="flex items-center gap-3 flex-wrap">
              {exercise.image_history.map((h, i) => (
                <div key={i} className="flex flex-col items-center gap-1" data-testid={`image-history-item-${i}`}>
                  <img src={`${backendUrl}${h.image_url}`} alt="" className="w-14 h-14 rounded-lg object-cover border border-white/10" />
                  <button onClick={() => setPendingRevertIdx(i)} disabled={revertingIdx === i}
                    className="text-[10px] font-bold text-violet-300 hover:text-white disabled:opacity-50 flex items-center gap-1"
                    data-testid={`image-history-revert-${i}`}>
                    {revertingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : L('Restaurer', 'Restore')}
                  </button>
                </div>
              ))}
            </div>
            {pendingRevertIdx != null && exercise.image_history[pendingRevertIdx] && (
              <div className="space-y-2 pt-1" data-testid="image-history-revert-compare-panel">
                <p className="text-[10px] font-bold text-amber-300 uppercase text-center">{L('Confirmer la restauration ?', 'Confirm restore?')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase text-center">{L('Actuelle', 'Current')}</p>
                    <img src={`${backendUrl}${exercise.image_url}`} alt="actuelle" className="w-full aspect-square object-contain rounded-lg bg-zinc-900 border border-white/10" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-amber-300 uppercase text-center">{L('Version à restaurer', 'Version to restore')}</p>
                    <img src={`${backendUrl}${exercise.image_history[pendingRevertIdx].image_url}`} alt="à restaurer" className="w-full aspect-square object-contain rounded-lg bg-zinc-900 border border-amber-500/40" data-testid="image-history-revert-preview" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { revertImage(pendingRevertIdx); setPendingRevertIdx(null); }} disabled={revertingIdx != null}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white bg-amber-600 hover:bg-amber-500 disabled:opacity-50 transition-colors"
                    data-testid="image-history-revert-confirm-btn">
                    {revertingIdx != null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {L('Confirmer la restauration', 'Confirm restore')}
                  </button>
                  <button onClick={() => setPendingRevertIdx(null)} disabled={revertingIdx != null}
                    className="px-3 py-2 rounded-lg text-xs font-bold text-zinc-400 hover:text-white transition-colors"
                    data-testid="image-history-revert-cancel-btn">
                    {L('Annuler', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* YouTube */}
      <div className="pt-3 border-t border-white/5 space-y-2">
        <p className="text-xs font-bold text-zinc-300 uppercase flex items-center gap-1.5"><Youtube className="w-3.5 h-3.5" /> {L('Vidéo YouTube', 'YouTube video')}</p>
        {exercise.youtube_url && (
          <a href={exercise.youtube_url} target="_blank" rel="noopener noreferrer"
            className="block text-xs text-primary underline break-all mb-1" data-testid="current-youtube-link">
            {exercise.youtube_url}
          </a>
        )}
        <button onClick={searchYoutube} disabled={searchingYt}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-zinc-800 border border-white/10 hover:border-red-500/40 disabled:opacity-50"
          data-testid="search-youtube-btn">
          {searchingYt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Youtube className="w-4 h-4" />}
          {exercise.youtube_url ? L('Changer de vidéo', 'Change video') : L('Rechercher une vidéo', 'Search a video')}
        </button>
        {ytCandidates && (
          <div className="space-y-1.5" data-testid="youtube-candidates-list">
            {ytCandidates.length === 0 && <p className="text-xs text-zinc-500">{L('Aucun résultat.', 'No results.')}</p>}
            {ytCandidates.map((c) => (
              <button key={c.video_id} onClick={() => attachYoutube(c.url)}
                className="flex items-center gap-2 w-full text-left p-2 rounded-lg bg-zinc-800/50 border border-white/10 hover:border-primary/40"
                data-testid={`youtube-candidate-${c.video_id}`}>
                <img src={c.thumbnail} alt="" className="w-16 h-10 object-cover rounded" />
                <span className="text-xs text-zinc-200 line-clamp-2">{c.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
